import { useContext } from 'react'

import { useSetState } from '@domain-group/react-hooks'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import useBrandCustomProfile from '@hooks/useBrandCustomProfile'
import useRole from '@hooks/useRole'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import { notificationTypes as notificationPayload } from '@functions/notification'

import PermissionList from './components/PermissionList/PermissionList'

const MODAL_KEY = 'ManageBrandCustomProfileUserPermissionsModal'

type ModalCallbacks = {
  closeAllModals: () => void,
  createBrandCustomProfile: (payload: HandleCallbackActionParams) => void,
  updateBrandCustomProfile: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

function transformNotificationTypesToPermissions(notificationTypes: typeof notificationPayload) {
  return Object.fromEntries(
    Object.entries(notificationTypes).map(([category, notifications]) => [
      category,
      notifications.map(n => ({
        key: n.key,
        is_enabled: true,
        title: n.title,
      })),
    ]),
  )
}

const agencyAndPropertyMarketingDefaultState = (isAgent: boolean) => ({
  artwork: [{ key: 'manage', is_enabled: true, title: 'Manage' },
    { key: 'approve', is_enabled: !isAgent, title: 'Approve' },
  ],
  comparables: [{ key: 'manage', is_enabled: true, title: 'Manage' }],
  contacts: [{ key: 'manage', is_enabled: true, title: 'Manage' }],
  details: [
    { key: 'manage', is_enabled: true, title: 'Manage' },
    { key: 'create', is_enabled: true, title: 'Create' },
  ],
  invoices: [
    { key: 'manage', is_enabled: !isAgent, title: 'Manage' },
    { key: 'approve', is_enabled: true, title: 'Approve' },
  ],
  orders: [
    { key: 'manage', is_enabled: true, title: 'Manage' },
    { key: 'approve', is_enabled: !isAgent, title: 'Approve' },
    { key: 'unapprove', is_enabled: true, title: 'Unapprove' },
    { key: 'delete', is_enabled: true, title: 'Delete' },
    { key: 'require_quote', is_enabled: true, title: 'Require Quote' },
    { key: 'order_total_override', is_enabled: true, title: 'Price Override' },
  ],
  photos_and_plans: [{ key: 'manage', is_enabled: true, title: 'Manage' }],
  ad_copy: [{ key: 'manage', is_enabled: true, title: 'Manage' }],
  media_selection: [{ key: 'manage', is_enabled: true, title: 'Manage' }],
  portals: [{ key: 'manage', is_enabled: true, title: 'Manage' }],
  publications: [{ key: 'manage', is_enabled: true, title: 'Manage' }],
  quotes: [
    { key: 'manage', is_enabled: true, title: 'Manage' },
    { key: 'approve', is_enabled: true, title: 'Approve' },
    { key: 'require_authorization', is_enabled: true, title: 'Require Authorization' },
    { key: 'require_contributions', is_enabled: true, title: 'Require Contributions' },
  ],
})

const notificationOptionsDefaultState = transformNotificationTypesToPermissions(notificationPayload)

const permissionDataDefaultState = (profileType: string, isAgent: boolean) => {
  if (profileType === 'notifications') return notificationOptionsDefaultState
  if (profileType === 'agency_marketing') return agencyAndPropertyMarketingDefaultState(isAgent)
  if (profileType === 'property_marketing') return agencyAndPropertyMarketingDefaultState(isAgent)
  return {}
}
const ManageBrandCustomProfileUserPermissionsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeAllModals, createBrandCustomProfile, updateBrandCustomProfile } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] ?? {}
  const {
    brandCustomProfileParams: brandCustomProfileFormPayloadForm,
    roleId: selectedRoleId,
  } = modalPayload

  const { role } = useRole({ id: selectedRoleId })

  const isAgent = (role && 'key' in role && role.key === 'agent') || false

  const { entityState } = brandCustomProfileFormPayloadForm

  const { id, profile_type: profileType, role_id: roleId } = entityState || {}

  const [state, setState] = useSetState({
    permissionData: id
      ? entityState.permission_data : permissionDataDefaultState(profileType, isAgent),
  })
  const { permissionData } = state

  const {
    callbacks: {
      createBrandCustomProfile: createFn,
      updateBrandCustomProfile: updateFn,
    },
    creating,
  } = useBrandCustomProfile(entityState)

  const brandCustomProfilePayload = {
    callbacks: {
      action: id ? updateFn : createFn,
      afterAction: closeAllModals,
    },
    entityParams: { ...entityState, permission_data: permissionData },
    toastText: `Brand Custom Profile ${id ? 'Updated' : 'Created'}`,
  }

  return (
    // @ts-expect-error TS2741
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      {/*
       // @ts-expect-error TS2559 */}
      <SidebarModal.Header callbacks={callbacks} title="Manage" titleSecondLine="User Permissions" />

      <SidebarModal.Content>
        <Form>
          <PermissionList
            permissionData={permissionData}
            profileType={profileType}
            setState={setState}
            selectedRoleId={roleId}
          />
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={creating}
          onClick={() => (id
            // @ts-expect-error TS2345
            ? updateBrandCustomProfile(brandCustomProfilePayload)
            // @ts-expect-error TS2345
            : createBrandCustomProfile(brandCustomProfilePayload))}
          size="large"
        >
          Save Permissions
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageBrandCustomProfileUserPermissionsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
